import {
  DistributionFileMetadata,
  DistributionTableRow,
} from '../../shared/models/distribution/distribution.model';

/** A tab of the distribution list (Primary, Red, Yellow, ...). */
export interface DistributionListGroup {
  id: string;
  name: string;
  /** Hex color, for example `#D9B100`. */
  color: string;
  /** Default groups are system-defined and cannot be renamed, recolored or deleted. */
  isDefault: boolean;
  /** Sheet of the list in the workbook; empty for a list created on the page and not saved yet. */
  sheetName?: string;
}

/** Location of the saved distribution list file, used to write edits back to it. */
export interface DistributionListSource {
  rigJournalId: string;
  distributionUrl: string;
  language: string;
}

/**
 * Distribution list file owned by the asset wizard. Edits are written into `file` in memory and
 * uploaded only when the wizard is saved.
 */
export interface DistributionListDraft {
  /** Wizard instance the draft belongs to (rig journal id, or a fixed key for a new asset). */
  key: string;
  file: File;
  isNewFile: boolean;
}

/**
 * What a user looks like on one tab (group). The same person can appear on
 * several tabs with different validation results and hover texts, so the
 * tab-specific values are kept per group.
 */
export interface DistributionListGroupRow
  extends Omit<
    DistributionTableRow,
    'id' | 'sendNotification' | 'groupIds' | 'selected'
  > {
  /** Index of the row inside its tab, used to keep the original row order. */
  position: number;
  /** HAL ID of the row in the uploaded file, when it has one. */
  halId?: string;
  /** True for a row created on the page, so it has no row in the uploaded file. */
  isNew?: boolean;
}

export interface DistributionListUser extends DistributionTableRow {
  id: string;
  /** Groups (sheets) where `SEND NOTIFICATIONS` is off for this user. Each sheet has its own value. */
  mutedGroupIds: string[];
  /** Ids of the groups (tabs) the user belongs to. */
  groupIds: string[];
  /** HAL ID of a user picked from the Microsoft Graph search, written to the HAL ID column of the xlsx. */
  halId?: string;
  /** Per tab values (display values, validation, hovers). Key is the group id. */
  rowsByGroup?: Record<string, DistributionListGroupRow>;
}

export interface DistributionListFile extends DistributionFileMetadata {}

export interface DistributionListData {
  file: DistributionListFile;
  groups: DistributionListGroup[];
  users: DistributionListUser[];
}

/** A user suggestion returned by the Microsoft Graph user search. */
export interface GraphUserSuggestion {
  id: string;
  displayName: string;
  email: string;
  company: string;
  title: string;
  phone: string;
  halId?: string;
  /** True when the user already has a profile in the system (role / discipline can be loaded). */
  existingInSystem?: boolean;
}

/** Title and phone loaded from the profile of a user that already exists in the system. */
export interface ExistingUserInfo {
  title: string;
  phone: string;
}

/** A user item returned by the backend `GET user/graph` endpoint. */
export interface GraphUserSearchItem {
  id?: string | null;
  halId?: string | null;
  displayName?: string | null;
  mail?: string | null;
  userPrincipalName?: string | null;
  jobTitle?: string | null;
  phone?: string | null;
  existingInSystem?: boolean | null;
}

/** Envelope returned by the placeholder API methods. */
export interface DistributionListApiResponse<T> {
  data: T;
  message?: string;
}

export type NewDistributionListUser = Omit<DistributionListUser, 'id'>;

/** `name` is set only when an existing user is added to more lists (the form data wins over the file rows). */
export type DistributionListUserPatch = Partial<
  Pick<DistributionListUser, 'name' | 'company' | 'title' | 'email' | 'phone' | 'groupIds'>
>;

/** Name and Phone as displayed in the single edit form (the disabled Name included); blank when empty or a placeholder. */
export interface DistributionListSharedFields {
  name: string;
  phone: string;
}

/** Patch emitted by the edit panel. `originalEmail` is set only when the email changed, to locate the Excel row. */
export type DistributionListUserEditPatch = DistributionListUserPatch & {
  originalEmail?: string;
};

/** Drops the lookup-only `originalEmail` so it is never merged into a user record. */
export function toUserPatch(patch: DistributionListUserEditPatch): DistributionListUserPatch {
  const changes = { ...patch };
  delete changes.originalEmail;
  return changes;
}

export function receivesNotifications(user: DistributionListUser, groupId: string): boolean {
  return !user.mutedGroupIds.includes(groupId);
}

export function withNotification(
  user: DistributionListUser,
  groupId: string,
  enabled: boolean
): DistributionListUser {
  const others = user.mutedGroupIds.filter(id => id !== groupId);
  return { ...user, mutedGroupIds: enabled ? others : [...others, groupId] };
}

export type UserPanelMode = 'add' | 'edit';

export type DistributionListPanel = UserPanelMode | 'customize' | null;
